/*
 * <sticky-buy-bar> — floating bottom purchase bar for the product page.
 *
 * - Reveals on scroll (IntersectionObserver) once the main add-to-cart button
 *   leaves the viewport; hides again when it returns.
 * - "Add to cart" and "Buy now" are PROXIES that .click() the real controls in
 *   the main product form, so behaviour matches the product page exactly
 *   (variant selection, cart drawer, Shop Pay / dynamic checkout). The click is
 *   dispatched inside the user's gesture, so redirects/popups stay allowed.
 * - Price is mirrored from the main price node; the add button mirrors the real
 *   button's disabled / sold-out state.
 */
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    this.observeTarget = document.querySelector(this.dataset.observe);
    this.submitSelector = this.dataset.submit;
    this.paymentSelector = this.dataset.payment;
    this.priceSource = document.querySelector(this.dataset.priceSource);
    this.cartUrl = this.dataset.cartUrl || '/cart';

    this.addBtn = this.querySelector('[data-sticky-add]');
    this.buyBtn = this.querySelector('[data-sticky-buy]');
    this.priceTarget = this.querySelector('[data-sticky-price]');

    // Take the bar out of the no-JS hidden state so CSS transforms can animate.
    this.removeAttribute('hidden');

    this.setupHeightSync();
    this.setupVisibility();
    this.setupPriceMirror();
    this.setupSubmitMirror();

    if (this.addBtn) {
      this.addBtn.addEventListener('click', () => this.proxyAddToCart());
    }
    if (this.buyBtn) {
      this.buyBtn.hidden = false;
      this.buyBtn.addEventListener('click', () => this.proxyBuyNow());
    }
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
    if (this.priceObserver) this.priceObserver.disconnect();
    if (this.submitObserver) this.submitObserver.disconnect();
    document.body.classList.remove('sticky-buy-bar-visible');
  }

  /* Publish the bar's height so other fixed UI (e.g. Back-to-top) can sit
     clear of it. Kept in sync as the layout/breakpoint changes. */
  setupHeightSync() {
    const setVar = () => {
      document.documentElement.style.setProperty('--sticky-buy-bar-height', `${this.offsetHeight}px`);
    };
    setVar();
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(setVar);
      this.ro.observe(this);
    } else {
      window.addEventListener('resize', setVar);
    }
  }

  /* Show the bar only while the main buy buttons are scrolled out of view. */
  setupVisibility() {
    const reflect = (visible) => {
      this.classList.toggle('is-visible', visible);
      document.body.classList.toggle('sticky-buy-bar-visible', visible);
    };
    if (!this.observeTarget || !('IntersectionObserver' in window)) {
      // Fallback: always visible once scrolled a little.
      reflect(true);
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          reflect(!entry.isIntersecting);
        });
      },
      { rootMargin: '0px 0px 0px 0px', threshold: 0 }
    );
    this.io.observe(this.observeTarget);
  }

  /* Mirror the live price (variant/sale aware) from the main price node. */
  setupPriceMirror() {
    if (!this.priceSource || !this.priceTarget) return;
    const sync = () => {
      this.priceTarget.innerHTML = this.priceSource.innerHTML;
    };
    sync();
    this.priceObserver = new MutationObserver(sync);
    this.priceObserver.observe(this.priceSource, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /* Mirror the real add-to-cart button's disabled state + label (sold out). */
  setupSubmitMirror() {
    const submit = document.querySelector(this.submitSelector);
    if (!submit || !this.addBtn) return;
    const label = this.addBtn.querySelector('span');
    const sync = () => {
      this.addBtn.disabled = submit.hasAttribute('disabled');
      const submitLabel = submit.querySelector('span');
      if (label && submitLabel) label.textContent = submitLabel.textContent.trim();
    };
    sync();
    this.submitObserver = new MutationObserver(sync);
    this.submitObserver.observe(submit, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  proxyAddToCart() {
    const submit = document.querySelector(this.submitSelector);
    if (submit && !submit.hasAttribute('disabled')) {
      submit.click();
    }
  }

  proxyBuyNow() {
    // Prefer the native dynamic-checkout (Shop Pay etc.) button when present.
    const payment = document.querySelector(this.paymentSelector);
    if (payment) {
      payment.click();
      return;
    }
    // Fallback: add to cart, then go to cart/checkout within the same gesture.
    const submit = document.querySelector(this.submitSelector);
    if (submit && !submit.hasAttribute('disabled')) {
      submit.click();
    }
    window.location.href = this.cartUrl;
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
